import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Filter, RotateCcw, Clock, Calendar
} from 'lucide-react';
import { toast } from 'react-toastify';
import { accountsService } from '../../../services/accountsService';
import customerMasterService from '../../../services/customerMasterService';
import Breadcrumb from '../../../components/Common/Breadcrumb';
import DataTable from '../../../components/DataTable/DataTable';

const InvoiceAgingReport = () => {
  const navigate = useNavigate();
  
  const [filters, setFilters] = useState({
    customer: '',
    asOfDate: new Date().toISOString().split('T')[0] // Today's date
  });
  
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);

  // Load customers for dropdown
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const response = await customerMasterService.getAllCustomers({ limit: 1000 });
        if (response && response.success) {
          setCustomers(response.data || []);
        }
      } catch (error) {
        console.error('Error loading customers:', error);
      }
    };
    loadCustomers();
  }, []);

  // Load report data
  useEffect(() => {
    loadReportData();
  }, [filters]);

  const loadReportData = async () => {
    setLoading(true);
    try {
      const params = {
        asOfDate: filters.asOfDate
      };
      
      if (filters.customer) {
        params.customer = filters.customer;
      }
      
      const response = await accountsService.getInvoiceAgingReport(params);

      if (response && response.success) {
        setReportData(response.data);
      } else {
        toast.error('Failed to load invoice aging report');
      }
    } catch (error) {
      console.error('Error loading invoice aging report:', error);
      toast.error('Failed to load invoice aging report');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleResetFilter = () => {
    setFilters({
      customer: '',
      asOfDate: new Date().toISOString().split('T')[0]
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Font sizes for report - matching Tax Invoice Report
  const reportFontSize = {
    label: '11px',
    input: '11px',
    footerLabel: '14px',
    footerValue: '20px'
  };

  // Define columns for DataTable
  const columns = [
    {
      key: 'serviceInvoiceNumber',
      label: 'Invoice #',
      sortable: true
    },
    {
      key: 'customerName',
      label: 'Customer Name',
      sortable: true
    },
    {
      key: 'customerCode',
      label: 'Customer Code',
      sortable: true,
      render: (value) => value || '-'
    },
    {
      key: 'serviceInvoiceDate',
      label: 'Invoice Date',
      sortable: true,
      render: (value) => formatDate(value)
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      sortable: true,
      render: (value) => formatDate(value)
    },
    {
      key: 'daysPastDue',
      label: 'Days Past Due',
      sortable: true,
      render: (value) => {
        if (value <= 0) return 'Not Due';
        return `${value} days`;
      }
    },
    {
      key: 'agingBucket',
      label: 'Aging Bucket',
      sortable: true,
      render: (value) => {
        const colors = {
          'Current': '#10b981',
          '0-30 Days': '#3b82f6',
          '31-60 Days': '#f59e0b',
          '61-90 Days': '#ef4444',
          'Over 90 Days': '#dc2626'
        };
        const color = colors[value] || '#6b7280';
        return (
          <span style={{ 
            color, 
            fontWeight: '600',
            padding: '4px 8px',
            borderRadius: '4px',
            backgroundColor: `${color}15`
          }}>
            {value}
          </span>
        );
      }
    },
    {
      key: 'totalAmountWithTax',
      label: 'Outstanding Amount',
      sortable: true,
      render: (value) => `₹ ${formatCurrency(value)}`
    },
    {
      key: 'paymentStatus',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const statusColors = {
          'Pending': '#f59e0b',
          'Overdue': '#ef4444',
          'Paid': '#10b981'
        };
        const color = statusColors[value] || '#6b7280';
        return (
          <span style={{ color, fontWeight: '500' }}>
            {value || 'Pending'}
          </span>
        );
      }
    },
    // Dummy actions column to prevent default action column from showing
    {
      key: 'actions',
      label: '',
      sortable: false,
      render: () => null
    }
  ];

  // Filter out the dummy actions column before passing to DataTable
  const visibleColumns = columns.filter(col => col.key !== 'actions');

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Accounts',
      path: '/accounts',
      onClick: () => navigate('/accounts')
    },
    {
      label: 'Reports',
      path: '/accounts?tab=reports',
      onClick: () => navigate('/accounts?tab=reports')
    },
    {
      label: 'Invoice Aging Report',
      path: '/accounts/reports/invoice-aging',
      isCurrent: true
    }
  ];

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 style={{ fontSize: '22px' }}>Invoice Aging Report</h1>
          <p style={{ fontSize: '12px' }}>Track outstanding invoices by aging buckets</p>
        </div>
      </div>

      {/* Filters Section */}
      <div style={{
        background: 'white',
        padding: '16px 20px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        borderRadius: '0'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto auto', gap: '10px', alignItems: 'end' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ marginBottom: '4px' }}>Select Customer</label>
            <select
              className="form-input"
              value={filters.customer}
              onChange={(e) => handleFilterChange('customer', e.target.value)}
              style={{ padding: '6px 10px' }}
            >
              <option value="">All Customers</option>
              {customers.map(customer => (
                <option key={customer._id || customer.id} value={customer._id || customer.id}>
                  {customer.customerName} ({customer.customerCode})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label required" style={{ marginBottom: '4px' }}>As Of Date</label>
            <input
              type="date"
              className="form-input"
              value={filters.asOfDate}
              onChange={(e) => handleFilterChange('asOfDate', e.target.value)}
              style={{ padding: '6px 10px' }}
            />
          </div>

          <button
            type="button"
            onClick={loadReportData}
            className="btn btn-primary"
            style={{ 
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            disabled={loading}
          >
            <Filter size={16} />
            Apply
          </button>

          <button
            type="button"
            onClick={handleResetFilter}
            className="btn btn-secondary"
            style={{ 
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <RotateCcw size={16} />
            Reset
          </button>
        </div>
      </div>

      {/* Summary Cards - Matching Dashboard Style */}
      {reportData && reportData.summary && (
        <div className="dashboard-metrics-grid" style={{ marginBottom: '20px' }}>
          <div className="metric-card revenue">
            <div className="metric-header">
              <div className="metric-icon">
                <Clock size={16} />
              </div>
            </div>
            <div className="metric-value">₹ {formatCurrency(reportData.summary.current)}</div>
            <div className="metric-title">Current (0-30 Days)</div>
            <div className="metric-subtitle">{reportData.agingBuckets.current.count} invoices</div>
          </div>

          <div className="metric-card customers">
            <div className="metric-header">
              <div className="metric-icon">
                <Clock size={16} />
              </div>
            </div>
            <div className="metric-value">₹ {formatCurrency(reportData.summary.days31to60)}</div>
            <div className="metric-title">31-60 Days</div>
            <div className="metric-subtitle">{reportData.agingBuckets.days31to60.count} invoices</div>
          </div>

          <div className="metric-card churn">
            <div className="metric-header">
              <div className="metric-icon">
                <Clock size={16} />
              </div>
            </div>
            <div className="metric-value">₹ {formatCurrency(reportData.summary.days61to90)}</div>
            <div className="metric-title">61-90 Days</div>
            <div className="metric-subtitle">{reportData.agingBuckets.days61to90.count} invoices</div>
          </div>

          <div className="metric-card burn">
            <div className="metric-header">
              <div className="metric-icon">
                <Clock size={16} />
              </div>
            </div>
            <div className="metric-value">₹ {formatCurrency(reportData.summary.over90)}</div>
            <div className="metric-title">Over 90 Days</div>
            <div className="metric-subtitle">{reportData.agingBuckets.over90.count} invoices</div>
          </div>

          <div className="metric-card revenue" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <div className="metric-header">
              <div className="metric-icon" style={{ color: 'white' }}>
                <Clock size={16} />
              </div>
            </div>
            <div className="metric-value" style={{ color: 'white' }}>₹ {formatCurrency(reportData.summary.total)}</div>
            <div className="metric-title" style={{ color: 'rgba(255,255,255,0.9)' }}>Total Outstanding</div>
            <div className="metric-subtitle" style={{ color: 'rgba(255,255,255,0.8)' }}>{reportData.invoices?.length || 0} invoices</div>
          </div>
        </div>
      )}

      {/* Data Table - Direct in parent container, no wrapper */}
      <div style={{ 
        marginBottom: '80px', 
        marginLeft: '0', 
        marginRight: '0',
        overflowX: 'hidden',
        width: '100%',
        maxWidth: '100%'
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>Loading invoice aging report...</div>
          </div>
        ) : reportData && reportData.invoices && reportData.invoices.length > 0 ? (
          <DataTable
            data={reportData.invoices}
            columns={visibleColumns}
            loading={loading}
            pagination={true}
            pageSize={50}
            noCard={true}
            className="invoice-aging-report-table"
            title=""
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Clock size={48} style={{ color: 'var(--color-text-muted, #8B93A7)', marginBottom: '16px' }} />
            <div style={{ color: 'var(--color-text-secondary, #5B6478)' }}>No outstanding invoices found</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceAgingReport;

